import React, { useState, useEffect } from "react";

// Centered Checklist App
// - Dark background (black), white card
// - Checklist with checkboxes, strike‑through on complete
// - Keyboard: Enter to add
// - Persists to localStorage

export default function App() {
  const [input, setInput] = useState("");
  const [items, setItems] = useState(() => {
    try { return JSON.parse(localStorage.getItem("checklist.items") || "[]"); } catch { return []; }
  });

  useEffect(() => { localStorage.setItem("checklist.items", JSON.stringify(items)); }, [items]);

  const addItem = () => {
    const text = input.trim();
    if (!text) return;
    setItems([{ id: cryptoRandom(), text, done: false }, ...items]);
    setInput("");
  };
  const toggleItem = (id) => setItems(items.map(it => it.id === id ? { ...it, done: !it.done } : it));
  const removeItem = (id) => setItems(items.filter(it => it.id !== id));
  const clearDone = () => setItems(items.filter(it => !it.done));

  return (
    <div style={styles.page}>
      <div style={styles.card}>
        <header style={styles.header}>
          <div style={styles.logo} aria-hidden>✅</div>
          <div>
            <h1 style={styles.title}>Today's Checklist</h1>
            <p style={styles.subtitle}>Focus on what matters most ✨</p>
          </div>
        </header>

        <div style={styles.row}>
          <input
            style={styles.input}
            placeholder="Add your task"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && addItem()}
            aria-label="Task"
          />
          <button style={styles.addBtn} onClick={addItem}>ADD</button>
        </div>

        {items.length === 0 ? (
          <div style={styles.empty}>No tasks yet! Add one above ⬆️</div>
        ) : (
          <ul style={styles.list}>
            {items.map(item => (
              <li key={item.id} style={{...styles.item, ...(item.done ? styles.itemDone : {})}}>
                <label style={styles.itemLabel}>
                  <input
                    type="checkbox"
                    checked={item.done}
                    onChange={() => toggleItem(item.id)}
                    style={styles.checkbox}
                    aria-label={item.text}
                  />
                  <span>{item.text}</span>
                </label>
                <button title="Remove" onClick={() => removeItem(item.id)} style={styles.deleteBtn}>🗑️</button>
              </li>
            ))}
          </ul>
        )}

        <footer style={styles.footer}>
          <span>{items.filter(i=>i.done).length}/{items.length} done</span>
          <div style={{flex:1}} />
          <button onClick={clearDone} style={styles.tertiaryBtn}>Clear completed</button>
        </footer>
      </div>
    </div>
  );
}

function cryptoRandom(){
  try { return crypto.randomUUID(); } catch { return Math.random().toString(36).slice(2); }
}

const styles = {
  page: {
    minHeight: "100vh",
    display: "grid",
    placeItems: "center",
    backgroundColor: "#000",
    fontFamily: 'Poppins, Segoe UI, system-ui, -apple-system, Arial, Helvetica, sans-serif',
    color: '#000',
    padding: 16,
  },
  card: {
    width: 'min(600px, 92vw)',
    background: '#fff',
    borderRadius: 18,
    padding: 20,
    boxShadow: '0 12px 30px -12px rgba(255,255,255,.25)'
  },
  header: { display: 'flex', alignItems: 'center', gap: 14, marginBottom: 12 },
  logo: { width: 56, height: 56, borderRadius: '50%', border: '2px solid #5d866c', display: 'grid', placeItems: 'center', background: '#fff', fontSize: 28 },
  title: { margin: 0, fontSize: 22, color: '#000' },
  subtitle: { margin: 0, opacity: .65, fontSize: 13, color: '#333' },
  row: { display: 'flex', gap: 10, alignItems: 'center', marginTop: 14 },
  input: { flex: 1, padding: '12px 14px', borderRadius: 12, border: '2px solid #5d866c', outline: 'none', fontSize: 16 },
  addBtn: { padding: '12px 16px', background: '#5d866c', color: '#fff', border: 0, borderRadius: 12, fontWeight: 600, cursor: 'pointer' },
  list: { listStyle: 'none', padding: 0, margin: '12px 0', display: 'grid', gap: 8 },
  item: { display: 'flex', alignItems: 'center', gap: 8, background: '#fff', border: '1px solid #ddd', borderRadius: 12, padding: '10px 12px' },
  itemDone: { opacity: .65, textDecoration: 'line-through' },
  itemLabel: { display: 'flex', alignItems: 'center', gap: 10, flex: 1, cursor: 'pointer', color: '#000' },
  checkbox: { width: 18, height: 18 },
  deleteBtn: { border: 0, background: 'transparent', cursor: 'pointer', fontSize: 18, padding: 6, borderRadius: 8 },
  empty: { textAlign: 'center', fontSize: 15, opacity: .85, background: '#fff', padding: 12, borderRadius: 12, marginTop: 10 },
  footer: { display: 'flex', alignItems: 'center', gap: 10, marginTop: 8, fontSize: 12, color: '#333' },
  tertiaryBtn: { border: 0, background: 'transparent', color: '#5d866c', cursor: 'pointer', fontWeight: 600 }
};
